import React from 'react';
import { ArrowTrendingDownIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useThemeStore } from '../../stores';
import { getChartPalette, formatDuration } from './chartTheme';
import { cn } from '../../utils/helpers';
import type { InsightsFunnelStep } from '../../types';

interface ConversionFunnelProps {
  steps: InsightsFunnelStep[];
  totalSessions: number;
}

/**
 * Horizontal funnel. Magnitude is encoded by bar length against a shared track,
 * so a single hue is correct here — the steps are one measure, not four series.
 * Every bar is directly labelled; the drop-off between steps sits in the gap.
 */
export const ConversionFunnel: React.FC<ConversionFunnelProps> = ({ steps, totalSessions }) => {
  const { theme } = useThemeStore();
  const colors = getChartPalette(theme === 'dark');

  if (!totalSessions) {
    return (
      <div className="flex h-[320px] items-center justify-center text-sm text-secondary-500 dark:text-secondary-400">
        No checkout sessions in this period yet.
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {steps.map((step, index) => {
        const width = totalSessions > 0 ? (step.reached / totalSessions) * 100 : 0;
        const isFinal = index === steps.length - 1;

        return (
          <div key={step.key}>
            <div className="flex items-baseline justify-between gap-3 mb-1.5">
              <span
                className={cn(
                  'text-sm font-medium truncate',
                  isFinal
                    ? 'text-success-700 dark:text-success-400'
                    : 'text-secondary-700 dark:text-secondary-300'
                )}
              >
                {step.label}
              </span>
              <span className="flex items-baseline gap-2 shrink-0">
                <span className="text-sm font-semibold tabular-nums text-secondary-900 dark:text-secondary-50">
                  {step.reached.toLocaleString()}
                </span>
                <span className="text-xs tabular-nums text-secondary-500 dark:text-secondary-400">
                  {step.conversionFromStart}%
                </span>
              </span>
            </div>

            <div
              className="h-8 w-full rounded-md overflow-hidden"
              style={{ backgroundColor: colors.track }}
            >
              <div
                className="h-full rounded-md transition-[width] duration-500 ease-out"
                // Length alone carries magnitude — no second, meaningless encoding.
                style={{
                  width: `${Math.max(width, step.reached > 0 ? 1.5 : 0)}%`,
                  backgroundColor: isFinal ? colors.series[2] : colors.series[0],
                }}
              />
            </div>

            {/* Drop-off annotation lives in the 2px gap between adjacent bars */}
            {!isFinal && (
              <div className="flex items-center gap-3 py-1.5 pl-1 text-xs text-secondary-500 dark:text-secondary-400">
                {steps[index + 1].droppedOff > 0 ? (
                  <span className="inline-flex items-center gap-1 text-danger-600 dark:text-danger-400">
                    <ArrowTrendingDownIcon className="h-3.5 w-3.5" />
                    <span className="tabular-nums font-medium">
                      {steps[index + 1].droppedOff.toLocaleString()} lost
                    </span>
                    <span className="tabular-nums">({steps[index + 1].dropOffRate}%)</span>
                  </span>
                ) : (
                  <span className="text-success-600 dark:text-success-400">No drop-off</span>
                )}
                {step.avgSecondsOnStep > 0 && (
                  <span className="inline-flex items-center gap-1">
                    <ClockIcon className="h-3.5 w-3.5" />
                    <span className="tabular-nums">{formatDuration(step.avgSecondsOnStep)} avg</span>
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ConversionFunnel;
